import { SendHorizontal } from 'lucide-react'
import type { KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { countChars } from '@/lib/text'

const MAX_CHARS = 2000

/**
 * 对话页底部的输入框。回车发送、Shift+回车换行 —— 聊天的习惯和发帖（Ctrl+回车）不一样。
 * 输入法正在拼字时的回车是「选字」，不能当成发送。
 */
export function MessageInput({
  onSend,
  sending,
}: {
  onSend: (content: string, done: () => void) => void
  sending: boolean
}) {
  const t = useT()
  const [value, setValue] = useState('')
  const ref = useRef<HTMLTextAreaElement>(null)
  const used = countChars(value)
  const canSend = value.trim().length > 0 && used <= MAX_CHARS && !sending

  useEffect(() => {
    const node = ref.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${Math.min(node.scrollHeight, 160)}px`
  }, [value])

  const submit = () => {
    if (!canSend) return
    onSend(value.trim(), () => {
      setValue('')
      ref.current?.focus()
    })
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
    event.preventDefault()
    submit()
  }

  return (
    <div className="flex items-end gap-2">
      <textarea
        ref={ref}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={onKeyDown}
        rows={1}
        placeholder={t('messages.placeholder')}
        aria-label={t('messages.placeholder')}
        className="min-h-10 flex-1 resize-none rounded-md border border-line-strong bg-surface px-3 py-2 text-[15px] outline-none focus:border-brand-ink"
      />
      {used > MAX_CHARS - 100 && (
        <span
          className={cn(
            'pb-2 text-[13px] tabular-nums',
            used > MAX_CHARS ? 'text-danger' : 'text-ink-muted',
          )}
        >
          {MAX_CHARS - used}
        </span>
      )}
      <button
        type="button"
        onClick={submit}
        disabled={!canSend}
        aria-label={t('messages.send')}
        className="flex size-10 shrink-0 items-center justify-center bg-brand text-on-brand px-corners [--px-step:2px] hover:bg-brand-hover disabled:opacity-40"
      >
        <SendHorizontal className="size-5" />
      </button>
    </div>
  )
}
